// Shared, opt-in content entrances. No script/support means normal visible content.
const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
const mobile = window.matchMedia('(max-width: 989px)');
const registered = new WeakSet();
const pending = new Set();
const sequences = new Map();
let observer;

function finish(element) {
  element.removeAttribute('data-elikmed-entrance');
  element.style.removeProperty('--elikmed-entrance-delay');
  pending.delete(element);
  observer?.unobserve(element);
}

function setDelays(elements) {
  // Measure untransformed layout boxes, so translated pending elements cannot change rows.
  const boxes = elements.map((element) => {
    const rect = element.getBoundingClientRect();
    const translated = element.getAttribute('data-elikmed-entrance') === 'pending' ? 20 : 0;
    return { element, top: rect.top - translated, bottom: rect.bottom - translated };
  }).sort((a, b) => a.top - b.top);
  let rowBottom = -Infinity;
  let step = -1;
  for (const box of boxes) {
    if (box.top >= rowBottom - 1) {
      step += 1;
      rowBottom = box.bottom;
    } else {
      rowBottom = Math.min(rowBottom, box.bottom);
    }
    if (!registered.has(box.element) || box.element.dataset.elikmedEntrance === 'pending') {
      box.element.style.setProperty('--elikmed-entrance-delay', `${250 + step * 100}ms`);
    }
  }
}

function initialize() {
  if (motion.matches || !('IntersectionObserver' in window)) return;
  try {
    observer ??= new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const element = entry.target;
        observer.unobserve(element);
        element.setAttribute('data-elikmed-entrance', 'active');
        element.addEventListener('animationend', () => finish(element), { once: true });
      }
    }, { threshold: 0, rootMargin: mobile.matches ? '0px 0px 5% 0px' : '0px 0px 20% 0px' });

    const groups = [
      ['.elikmed-group--hero-copy', 'h1', '.paragraph', '.button'],
      ['.elikmed-home--equipment .elikmed-group--section-heading', 'h2', '.paragraph'],
      ['.elikmed-home--products .section-resource-list__header', 'h2', '.button-unstyled'],
      ['.elikmed-group--partner-copy', 'h2', '.paragraph', '.button-secondary'],
      ['.elikmed-home--services', '.elikmed-group--section-heading h2', '.elikmed-group--section-heading .paragraph', '.button-unstyled'],
      ['.elikmed-group--quote-panel', 'h2', '.paragraph', '.button-custom'],
    ];
    // Explicit content groups keep cards, forms, commerce controls and app blocks static.
    groups.push(
      ['.elikmed-about--hero', 'h1', '.paragraph'],
      ['.elikmed-about--experience', '.custom-typography:last-child p'],
      ['.elikmed-about--equipment', 'h2', '.paragraph', 'a.button, a.button-secondary, a.button-unstyled'],
      ['.elikmed-about--manufacturers', 'h2', '.paragraph', 'a.button, a.button-secondary'],
      ['.elikmed-services--hero', 'h1', '.paragraph'],
      ['.elikmed-services--service', 'h2', '.custom-typography p'],
      ['.elikmed-quote--steps', 'h2'],
      ['.elikmed-quote--concierge', 'h2', '.paragraph'],
      ['.elikmed-contact__main', 'h1', '.elikmed-contact__intro'],
      ['.elikmed-collection-hero__text', 'h1', '.elikmed-collection-hero__description'],
      ['.elikmed-product .product-details', 'h1', '.elikmed-product-description__preview'],
      ['.elikmed-product-help__panel', 'h2', '.paragraph', '.elikmed-product-help__link'],
      ['.section-resource-list__content', ':scope > .text-block h2, :scope > .group-block h1', ':scope > .elikmed-product-all-link'],
      ['.page-width-content .section-content-wrapper', ':scope > .text-block h1'],
    );
    for (const [scope, ...selectors] of groups) {
      for (const root of document.querySelectorAll(`#MainContent ${scope}`)) {
        const elements = [...new Set(selectors.flatMap((selector) => {
          // About equipment's first paragraph is its intro; the rest are utility labels.
          const matches = scope === '.elikmed-about--equipment' && selector === '.paragraph'
            ? [root.querySelector(selector)].filter(Boolean) : [...root.querySelectorAll(selector)];
          // Both Hero buttons retain their shared action wrapper as one step.
          return matches.map((element) => scope === '.elikmed-group--hero-copy' && selector === '.button'
            ? element.closest('.group-block') : element);
        }))].filter(Boolean);
        setDelays(elements);
        sequences.set(root, elements);
        for (const element of elements) {
          if (registered.has(element)) continue;
          registered.add(element);
          pending.add(element);
          observer.observe(element);
          element.setAttribute('data-elikmed-entrance', 'pending');
        }
      }
    }
  } catch {
    pending.forEach(finish);
  }
}

motion.addEventListener('change', () => {
  if (motion.matches) pending.forEach(finish);
  else initialize();
});
mobile.addEventListener('change', () => {
  observer?.disconnect();
  observer = undefined;
  initialize();
  // Update only unrevealed elements; active/completed entrances must not restart.
  for (const element of pending) {
    if (element.dataset.elikmedEntrance === 'pending') observer?.observe(element);
  }
});
window.addEventListener('resize', () => {
  for (const [root, elements] of sequences) {
    if (root.isConnected) setDelays(elements);
    else sequences.delete(root);
  }
}, { passive: true });
document.addEventListener('focusin', (event) => {
  const element = event.target.closest('[data-elikmed-entrance]');
  if (element) finish(element);
});
document.addEventListener('shopify:section:load', initialize);
initialize();
